import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, FileText, Banknote } from "lucide-react";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { toast } from "sonner";
import { Skeleton } from "../../components/ui/skeleton";
import { Button } from "../../components/ui/button";
import { ErrorState } from "../../components/shared/ErrorState";
import { formatCurrency, formatEpoch } from "../../lib/utils";
import { useTaxConfig, breakdownFromPayment } from "../../lib/tax";
import { ReceiptDialog } from "../../components/payments/ReceiptDialog";
import { receiptFromPayment } from "../../components/payments/PaymentReceipt";

function statusColor(label) {
  if (label === "Approved") return "bg-success-bg text-success";
  if (label === "Pending") return "bg-warning-bg text-warning";
  return "bg-deactivated-bg text-deactivated";
}

export default function CustomerPayments() {
  const [state, setState] = useState({ loading: true, error: null, items: [] });
  const [payingId, setPayingId] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const taxCfg = useTaxConfig();
  const navigate = useNavigate();

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    apiClient
      .get("/customer/payments")
      .then(({ data }) => setState({ loading: false, error: null, items: data }))
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load your payments."), items: [] }));
  };

  useEffect(() => { load(); }, []);

  const payNow = async (p) => {
    setPayingId(p.id);
    try {
      const { data } = await apiClient.post(`/customer/payments/${p.id}/pay`);
      navigate(`/pay/${data.payment_link.token}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not start payment. Please try again."));
      setPayingId(null);
    }
  };

  return (
    <div data-testid="customer-payments-page">
      <h1 className="mb-4 font-heading text-2xl font-bold tracking-tight text-text-main">Payments</h1>
      {state.loading ? (
        <>
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="mt-3 h-20 w-full rounded-2xl" />
        </>
      ) : state.error ? (
        <ErrorState message={state.error} onRetry={load} />
      ) : state.items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-surface py-14 text-center text-sm text-text-muted" data-testid="customer-payments-empty">
          No payments yet.
        </div>
      ) : (
        <div className="space-y-3">
          {state.items.map((p) => {
            const bd = breakdownFromPayment(p, taxCfg);
            const isPending = p.payable;
            const isApproved = p.status === 2;
            // Cash-selected pending: collected by the driver on delivery, not an
            // online debt — show "Cash collection pending" instead of "Pay now".
            const isCashPending = p.status === 1 && String(p.payment_method) === "1";
            return (
              <div
                key={p.id}
                className={`rounded-2xl border border-border bg-surface p-4 shadow-sm ${isApproved ? "cursor-pointer transition-all active:scale-[0.99] hover:border-primary/40" : ""}`}
                onClick={isApproved ? () => setReceipt(receiptFromPayment(p, bd)) : undefined}
                data-testid={`customer-payment-card-${p.id}`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-text-main">{p.purpose_label || "Payment"}</p>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${isCashPending ? "bg-warning-bg text-warning" : statusColor(p.status_label)}`}>
                    {isCashPending ? "Cash collection pending" : p.status_label}
                  </span>
                </div>

                <div className="mt-1.5 flex items-baseline gap-2">
                  <p className="font-heading text-xl font-bold text-primary">{formatCurrency(bd.total)}</p>
                  {bd.hasDiscount && (
                    <span className="text-sm text-text-muted line-through" data-testid={`customer-payment-original-${p.id}`}>
                      {formatCurrency(bd.initial)}
                    </span>
                  )}
                </div>

                {bd.hasDiscount && (
                  <p className="text-xs font-medium text-success" data-testid={`customer-payment-discount-${p.id}`}>
                    You save {formatCurrency(bd.discount)}
                  </p>
                )}
                {bd.enabled && (
                  <p className="text-xs text-text-muted" data-testid={`customer-payment-gst-${p.id}`}>
                    {formatCurrency(bd.subtotal)} + {formatCurrency(bd.gst)} GST
                  </p>
                )}
                <p className="mt-1 text-xs text-text-muted">{formatEpoch(p.created_at)} · {p.payment_method_label}</p>

                {isApproved && (
                  <p className="mt-2 flex items-center gap-1 text-xs font-medium text-primary" data-testid={`customer-payment-view-receipt-${p.id}`}>
                    <FileText size={13} strokeWidth={1.75} /> Tap to view receipt
                  </p>
                )}

                {isCashPending ? (
                  <div className="mt-3" data-testid={`customer-payment-cash-pending-${p.id}`}>
                    <p className="flex items-center gap-1.5 text-xs text-text-muted">
                      <Banknote size={13} strokeWidth={1.75} className="shrink-0" />
                      Our driver will collect the cash when your tiffin is delivered.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-2 w-full"
                      onClick={(e) => { e.stopPropagation(); payNow(p); }}
                      disabled={payingId === p.id}
                      data-testid={`customer-pay-online-instead-button-${p.id}`}
                    >
                      <CreditCard size={16} strokeWidth={1.75} />
                      {payingId === p.id ? "Starting…" : "Pay online instead"}
                    </Button>
                  </div>
                ) : isPending ? (
                  <Button
                    type="button"
                    className="mt-3 w-full"
                    onClick={(e) => { e.stopPropagation(); payNow(p); }}
                    disabled={payingId === p.id}
                    data-testid={`customer-pay-now-button-${p.id}`}
                  >
                    <CreditCard size={16} strokeWidth={1.75} />
                    {payingId === p.id ? "Starting…" : `Pay ${formatCurrency(bd.total)}`}
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      <ReceiptDialog open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)} data={receipt} />
    </div>
  );
}
